export class CryptographyHelper
{
    enableCache: boolean = true;
    enableLogging: boolean = false;

    private keyCache = new Map<string, CryptoKey>();
    private saltForPassword = new Map<string, Uint8Array>();
    private cumulativeEncryptMs: number = 0;
    private cumulativeDecryptMs: number = 0;

    private getSaltKey = (password: string, salt: Uint8Array): string => {
        return password + ':' + btoa(Array.from(salt).map(c => String.fromCharCode(c)).join(''));
    };

    getMessageEncoding = (message: string): Uint8Array => {
        const enc = new TextEncoder();
        return enc.encode(message);
    };

    getMessageDecoding = (dataIn: ArrayBuffer): string => {
        const enc = new TextDecoder();
        return enc.decode(dataIn);
    };

    generateAESKeyFromPassword = async (password: string, saltIn: ArrayBuffer) => {
        const encoder = new TextEncoder();
        const passwordKey = await crypto.subtle.importKey('raw', encoder.encode(password), 'PBKDF2', false, ['deriveKey']);
        return await crypto.subtle.deriveKey({
            name: 'PBKDF2',
            salt: saltIn,
            iterations: 100000,
            hash: 'SHA-256',
        }, passwordKey, {
            name: 'AES-GCM',
            length: 256,
        }, true, ['encrypt', 'decrypt']);
    };

    encryptValue = async (message: string, password: string): Promise<string> => {
        if (!message) return "";
        if (message.startsWith("ENC:")) return message;

        const t0 = this.enableLogging ? performance.now() : 0;

        let salt: Uint8Array;
        let key: CryptoKey;

        if (this.enableCache) {
            let cachedSalt = this.saltForPassword.get(password);
            if (!cachedSalt) {
                cachedSalt = window.crypto.getRandomValues(new Uint8Array(16));
                this.saltForPassword.set(password, cachedSalt);
            }
            salt = cachedSalt;

            const cacheKey = this.getSaltKey(password, salt);
            let cachedKey = this.keyCache.get(cacheKey);
            if (!cachedKey) {
                cachedKey = await this.generateAESKeyFromPassword(password, salt.buffer as ArrayBuffer);
                this.keyCache.set(cacheKey, cachedKey);
            }
            key = cachedKey;
        } else {
            salt = window.crypto.getRandomValues(new Uint8Array(16));
            key = await this.generateAESKeyFromPassword(password, salt.buffer as ArrayBuffer);
        }

        const encoded = this.getMessageEncoding(message);
        const iv = window.crypto.getRandomValues(new Uint8Array(12));
        const outE = await window.crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, encoded.buffer as ArrayBuffer);

        const combined = new Uint8Array(16 + 12 + outE.byteLength);
        combined.set(salt);
        combined.set(iv, 16);
        combined.set(new Uint8Array(outE), 16 + 12);

        let strRawDawg = '';
        for (let i = 0; i < combined.length; i++) {
            strRawDawg += String.fromCharCode(combined[i]);
        }

        if (this.enableLogging) {
            const elapsed = performance.now() - t0;
            this.cumulativeEncryptMs += elapsed;
            console.log(`[CryptographyHelper] encryptValue: ${elapsed.toFixed(2)}ms | cumulative encrypt: ${this.cumulativeEncryptMs.toFixed(2)}ms`);
        }

        return "ENC:" + btoa(strRawDawg);
    };

    decryptValue = async (cipherValue: string, password: string): Promise<string> => {
        if (!cipherValue) return "";

        const t0 = performance.now();

        const loadToBuffer = new Uint8Array(atob(cipherValue.substring(4)).split("").map(c => c.charCodeAt(0)));
        const salt = loadToBuffer.slice(0, 16);
        const iv = loadToBuffer.slice(16, 28);
        const ciphertextArray = loadToBuffer.slice(28);

        let key: CryptoKey;

        if (this.enableCache) {
            const cacheKey = this.getSaltKey(password, salt);
            let cachedKey = this.keyCache.get(cacheKey);
            if (!cachedKey) {
                cachedKey = await this.generateAESKeyFromPassword(password, salt.buffer as ArrayBuffer);
                this.keyCache.set(cacheKey, cachedKey);
            }
            key = cachedKey;
        } else {
            key = await this.generateAESKeyFromPassword(password, salt.buffer as ArrayBuffer);
        }

        const decrypted = await window.crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ciphertextArray);

        if (this.enableLogging) {
            const elapsed = performance.now() - t0;
            this.cumulativeDecryptMs += elapsed;
            console.log(`[CryptographyHelper] decryptValue: ${elapsed.toFixed(2)}ms | cumulative decrypt: ${this.cumulativeDecryptMs.toFixed(2)}ms`);
        }

        return this.getMessageDecoding(decrypted);
    };

}